import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_RESPOND_CHECKLIST } from '../../utils/constants';
import { AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Respond() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Incident Response'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-4373">
      <div className="container mx-auto px-4 py-12" data-id="element-4374">
        {/* Header */}
        <div className="mb-12" data-id="element-4375">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-4376">
            <AlertTriangle className="h-3 w-3 mr-2" data-id="element-4377" />
            Domain 5 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4378">
            Incident Response
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4379">
            Prepare to respond effectively when security incidents occur with
            clear procedures, defined roles, and practiced workflows.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4380">
          <div className="lg:col-span-2 space-y-12" data-id="element-4381">
            {/* What This Domain Covers */}
            <section data-id="element-4382">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4383">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-4384">
                <li className="flex items-start" data-id="element-4385">
                  <span className="text-cyan-500 mr-2" data-id="element-4386">•</span>
                  Incident response roles and escalation paths
                </li>
                <li className="flex items-start" data-id="element-4387">
                  <span className="text-cyan-500 mr-2" data-id="element-4388">•</span>
                  Containment procedures for common incidents
                </li>
                <li className="flex items-start" data-id="element-4389">
                  <span className="text-cyan-500 mr-2" data-id="element-4390">•</span>
                  Communication plans (internal and external)
                </li>
                <li className="flex items-start" data-id="element-4391">
                  <span className="text-cyan-500 mr-2" data-id="element-4392">•</span>
                  Evidence preservation and documentation
                </li>
                <li className="flex items-start" data-id="element-4393">
                  <span className="text-cyan-500 mr-2" data-id="element-4394">•</span>
                  Post-incident review and improvement
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-4395">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4396">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-4397">
                <ControlItem title="Appoint Incident Commander" description="Designate one person to lead incident response and make decisions." data-id="element-4398" />
                <ControlItem title="Create Contact List" description="Document who to call for different incident types (IT, Legal, HR, Leadership)." data-id="element-4399" />
                <ControlItem title="Document Common Scenarios" description="Write step-by-step procedures for ransomware, phishing, account takeover, data breach." data-id="element-4400" />
                <ControlItem title="Establish Communication Plan" description="Define how to notify employees, customers, and regulators during incidents." data-id="element-4401" />
                <ControlItem title="Preserve Evidence" description="Document what happened, take screenshots, save logs before making changes." data-id="element-4402" />
                <ControlItem title="Conduct Post-Incident Review" description="After every incident, document lessons learned and update procedures." data-id="element-4403" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-4404">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4405">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-4406">
                <ControlItem title="Create Incident Response Plan" description="Comprehensive document covering detection, containment, eradication, recovery, and lessons learned." data-id="element-4407" />
                <ControlItem title="Define Severity Levels" description="Classify incidents as Critical, High, Medium, Low with corresponding response times." data-id="element-4408" />
                <ControlItem title="Establish War Room Procedures" description="Define how the response team assembles, communicates, and coordinates during major incidents." data-id="element-4409" />
                <ControlItem title="Create Incident Runbooks" description="Detailed playbooks for specific scenarios (ransomware, DDoS, insider threat, data breach)." data-id="element-4410" />
                <ControlItem title="Conduct Tabletop Exercises" description="Practice incident response quarterly with realistic scenarios and time pressure." data-id="element-4411" />
                <ControlItem title="Establish Legal & PR Coordination" description="Define when to involve legal counsel and how to manage public communications." data-id="element-4412" />
                <ControlItem title="Implement Incident Tracking" description="Use ticketing system to track incidents, actions taken, and time to resolution." data-id="element-4413" />
                <ControlItem title="Define Escalation Triggers" description="Specify when to escalate to leadership, law enforcement, or external experts." data-id="element-4414" />
                <ControlItem title="Maintain Incident Response Kit" description="Pre-configured tools, contact lists, and procedures ready for immediate use." data-id="element-4415" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4416">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4417">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-4418" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-4419">
                <p className="flex items-start" data-id="element-4420">
                  <span className="text-red-500 mr-2" data-id="element-4421">✗</span>
                  Panicking and making hasty decisions (follow your plan, don't
                  improvise under pressure)
                </p>
                <p className="flex items-start" data-id="element-4422">
                  <span className="text-red-500 mr-2" data-id="element-4423">✗</span>
                  Immediately shutting down systems (may destroy evidence or
                  make recovery harder)
                </p>
                <p className="flex items-start" data-id="element-4424">
                  <span className="text-red-500 mr-2" data-id="element-4425">✗</span>
                  Not documenting actions taken (you'll need this for
                  investigation and compliance)
                </p>
                <p className="flex items-start" data-id="element-4426">
                  <span className="text-red-500 mr-2" data-id="element-4427">✗</span>
                  Hiding incidents from leadership (transparency is critical for
                  proper response)
                </p>
                <p className="flex items-start" data-id="element-4428">
                  <span className="text-red-500 mr-2" data-id="element-4429">✗</span>
                  Never testing your plan (untested plans fail when you need
                  them most)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-4430">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4431">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-4432" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-4433">
                <QuickWin day="Day 1-2" action="Appoint incident commander and create emergency contact list" data-id="element-4434" />
                <QuickWin day="Day 3-4" action="Write 1-page procedure for account takeover and ransomware" data-id="element-4435" />
                <QuickWin day="Day 5-6" action="Define when to notify leadership, legal, and customers" data-id="element-4436" />
                <QuickWin day="Day 7" action="Schedule first tabletop exercise (phishing scenario)" data-id="element-4437" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4438">
            <Checklist id="org-respond" title="Implementation Checklist" items={ORG_RESPOND_CHECKLIST} data-id="element-4439" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4440">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4441">
                First 24 Hours Priority
              </h3>
              <ol className="space-y-2 text-sm text-slate-300" data-id="element-4442">
                <li className="flex items-start" data-id="element-4443">
                  <span className="text-cyan-500 mr-2 font-bold" data-id="element-4444">1.</span>
                  Contain the incident (isolate affected systems)
                </li>
                <li className="flex items-start" data-id="element-4445">
                  <span className="text-cyan-500 mr-2 font-bold" data-id="element-4446">2.</span>
                  Notify incident commander and key stakeholders
                </li>
                <li className="flex items-start" data-id="element-4447">
                  <span className="text-cyan-500 mr-2 font-bold" data-id="element-4448">3.</span>
                  Preserve evidence (logs, screenshots, timeline)
                </li>
                <li className="flex items-start" data-id="element-4449">
                  <span className="text-cyan-500 mr-2 font-bold" data-id="element-4450">4.</span>
                  Assess scope and impact
                </li>
                <li className="flex items-start" data-id="element-4451">
                  <span className="text-cyan-500 mr-2 font-bold" data-id="element-4452">5.</span>
                  Begin eradication and recovery
                </li>
              </ol>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4453">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4454">
                Download Resources
              </h3>
              <Link to="/downloads/incident-runbooks" data-id="element-4455">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4456">
                  <Download className="h-4 w-4 mr-2" data-id="element-4457" />
                  Incident Runbooks
                </Button>
              </Link>
              <Link to="/ir/account-takeover" data-id="element-4458">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4459">
                  <Download className="h-4 w-4 mr-2" data-id="element-4460" />
                  Account Takeover Guide
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4461">
                <Button variant="secondary" fullWidth data-id="element-4462">
                  <Download className="h-4 w-4 mr-2" data-id="element-4463" />
                  Response Plan Template
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4464">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4465">Next Steps</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-4466">
                Complete your security program with recovery planning and
                continuous improvement.
              </p>
              <Link to="/organization/recover" data-id="element-4467">
                <Button variant="outline" fullWidth data-id="element-4468">
                  Next: Recovery & Improvement
                  <ArrowRight className="h-4 w-4 ml-2" data-id="element-4469" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-4470">
          <Link to="/organization/detect" data-id="element-4471">
            <Button variant="outline" data-id="element-4472">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4473" />
              Previous: Detection
            </Button>
          </Link>
          <Link to="/organization/recover" data-id="element-4474">
            <Button data-id="element-4475">
              Next Domain
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-4476" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-4477">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-4478">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-4479">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-4480">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-4481">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-4482">{action}</span>
    </div>;
}